import type {LucideIcon} from 'lucide-react';
import {MixModel, ProjectTab, ProjectTabType} from '../../../api/types.ts';
import {getTabIcon} from '../../../lib/tabIcons.ts';
import ProjectTabNames from '../ProjectTabNames.ts';
import getDefaultTabIcon from './getDefaultTabIcon.ts';

export interface ProjectTabSlot {
    /** 1-5, the same as ProjectDataIndex. */
    slot: number;
    /** What ?tab= takes: 'project' for slot 1, else 'tab2'...'tab5'. */
    name: string;
    tab: ProjectTab;
    title: string;
    icon: LucideIcon | undefined;
    canFullscreen: boolean;
}

// Every kind of tab has a full screen (focus mode) view; only the settings,
// sharing and other built-in tabs, which have no type, don't.
export function supportsFullscreen(type?: ProjectTabType | null) {
    return type !== undefined && type !== null;
}

export function tabNameForSlot(slot: number) {
    return slot === 1 ? ProjectTabNames.Project : `tab${slot}`;
}

// The other way round: 1-5 for a content tab's name, null for the rest.
export function slotForTabName(name: string): number | null {
    if (name === ProjectTabNames.Project) {
        return 1;
    }
    const match = /^tab([2-5])$/.exec(name);
    return match ? Number(match[1]) : null;
}

// The content tabs a viewer sees, in order: slot 1 always, the others when
// they are enabled. Titles fall back the same way the tab strip does.
export function projectTabSlots(
    project: Pick<MixModel, 'tabs'>
): ProjectTabSlot[] {
    const tabs = project.tabs;
    if (!tabs) {
        return [];
    }

    return [tabs.tab1, tabs.tab2, tabs.tab3, tabs.tab4, tabs.tab5].flatMap(
        (tab, index) => {
            const slot = index + 1;
            if (!tab || (slot > 1 && !tab.enabled)) {
                return [];
            }

            return [
                {
                    slot,
                    name: tabNameForSlot(slot),
                    tab,
                    title:
                        tab.title ?? (slot === 1 ? 'Project' : `Tab ${slot}`),
                    icon: getTabIcon(tab.icon ?? getDefaultTabIcon(tab.type)),
                    canFullscreen: supportsFullscreen(tab.type)
                }
            ];
        }
    );
}
